// src/components/OrganizationPlaceId.jsx
// Reusable component to search/select a Google Place and store its place_id for an organization.
// Dependencies: `@react-google-maps/api` (npm i @react-google-maps/api)
// Env: REACT_APP_GOOGLE_MAPS_API_KEY must be set.
// Why comments: explain choices or non-obvious decisions only.

import React, { useCallback, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Label,
  Input,
  Spinner,
} from "reactstrap"
import { LoadScript, StandaloneSearchBox } from "@react-google-maps/api"
import SnackbarComponent from "components/SnackBar"
import { updateOrganizationRequest } from "store/Organization/actions"
import PrimaryButton from "newcomponents/primary-button"

const LIBRARIES = ["places"] // why: only load what's needed to keep bundle light
const PLACE_ID_FINDER_URL =
  "https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder" // official tool for manual copy

function OrganizationPlaceId({ organizationId, initialPlaceId = "", onSaved }) {
  const dispatch = useDispatch()
  const userData = useSelector(s => s.Login?.userData)
  const mode = useSelector(state => state.Layout.layoutMode)
  const [placeIdValue, setPlaceIdValue] = useState(initialPlaceId)
  const [selectedPlace, setSelectedPlace] = useState(null)
  const [query, setQuery] = useState("")
  const [saving, setSaving] = useState(false)
  const [showSnackOk, setShowSnackOk] = useState(false)
  const [showSnackErr, setShowSnackErr] = useState(false)

  const searchBoxRef = useRef(null)

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  const mapsReady = Boolean(apiKey)

  const mapsUrl = useMemo(() => {
    if (!placeIdValue) return null
    return `https://www.google.com/maps/place/?q=place_id:${placeIdValue}`
  }, [placeIdValue])

  const onPlacesChanged = useCallback(() => {
    if (!searchBoxRef.current) return
    const places = searchBoxRef.current.getPlaces()
    if (!places || places.length === 0) return
    const place = places[0]
    // why: capture minimal fields we care about to keep component simple
    setSelectedPlace({
      name: place.name,
      address: place.formatted_address,
      place_id: place.place_id,
    })
    setPlaceIdValue(place.place_id || "")
  }, [])

  const handleSave = async () => {
    if (!organizationId) return
    if (!placeIdValue || typeof placeIdValue !== "string") return
    try {
      setSaving(true)
      // why: reuse existing write path so reducers/toasts stay consistent across the app
      await dispatch(
        updateOrganizationRequest({
          organizationId,
          googlePlaceId: placeIdValue,
        }),
      )
      setShowSnackOk(true)
      if (onSaved) onSaved(placeIdValue)
    } catch (e) {
      // dispatch likely handles errors; still show local feedback
      setShowSnackErr(true)
    } finally {
      setSaving(false)
      // snackbars in this codebase auto-reset via redux flags; we keep local ones short-lived
      setTimeout(() => {
        setShowSnackOk(false)
        setShowSnackErr(false)
      }, 2500)
    }
  }

  const handleClear = () => {
    setSelectedPlace(null)
    setPlaceIdValue("")
    setQuery("")
  }

  const disabledSave = !placeIdValue || saving

  return (
    <Card
      style={{
        borderBottomLeftRadius: "20px",
        borderBottomRightRadius: "20px",
        boxShadow:"none"
      }}
      className="mt-4"
    >
      <CardBody>
        <h4 className="card-title">Google Place ID</h4>
        <span className="text-muted small mb-4">
          For collecting Google Reviews, you need to link a Google Place to this
          organization. You can either search for it using the autocomplete
          field (if your business is listed on Google), or paste the Place ID
          manually.
        </span>
        <Row className="align-items-start g-3 mt-2">
          <Col md={12}>
            {!mapsReady ? (
              <div className="mb-2">
                <Label className="form-label">Place ID (manual)</Label>
                <Input
                  type="text"
                  placeholder="Paste Google Place ID"
                  value={placeIdValue}
                  onChange={e => setPlaceIdValue(e.target.value.trim())}
                />
                <div className="d-flex align-items-center gap-2 mt-1">
                  <a
                    href={PLACE_ID_FINDER_URL}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "#2990DF",
                    }}
                  >
                    Open official Place ID Finder
                  </a>
                </div>
              </div>
            ) : (
              <LoadScript googleMapsApiKey={apiKey} libraries={LIBRARIES}>
                <Label className="form-label">
                  Search business (Google Places)
                </Label>
                <StandaloneSearchBox
                  onLoad={ref => (searchBoxRef.current = ref)}
                  onPlacesChanged={onPlacesChanged}
                >
                  <Input
                    type="text"
                    placeholder="Start typing the company / address"
                    className="mb-2"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </StandaloneSearchBox>
                <div className="text-muted small">
                  Using Google Autocomplete. Only the selection is saved; no
                  Place Details call to minimize costs.
                </div>
              </LoadScript>
            )}
            {/* <LoadScript googleMapsApiKey={apiKey} libraries={LIBRARIES}>
                <StandaloneSearchBox
                  onPlacesChanged={onPlacesChanged}>
                
                  <Input
                    type="text"
                    placeholder="Start typing the company / address"
                    className="mb-2"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </StandaloneSearchBox>
              </LoadScript> */}
          </Col>

          <Col md={12}>
            <Label className="form-label">Selected Place ID</Label>
            <Input
              type="text"
              value={placeIdValue}
              placeholder="Will populate after selecting a place or paste manually"
              onChange={e => setPlaceIdValue(e.target.value.trim())}
            />
            {placeIdValue && (
              <div className="mt-2">
                <a
                  href={mapsUrl}
                  style={{
                    color: "#2990DF",
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Google Maps
                </a>
              </div>
            )}
          </Col>

          {selectedPlace && (
            <Col md={12}>
              <div className="p-2 border rounded">
                <div className="fw-semibold">{selectedPlace.name}</div>
                <div className="text-muted small">{selectedPlace.address}</div>
                <div className="text-muted small">
                  place_id: {selectedPlace.place_id}
                </div>
              </div>
            </Col>
          )}

          {initialPlaceId && (
            <Col md={12}>
              <div className="mt-2 text-muted small">
                Current saved place_id: <code>{initialPlaceId}</code>
              </div>
            </Col>
          )}

          <Col md={12} className="d-flex gap-2 mt-2">
            <PrimaryButton
              style={{
                width: "120px",
                height: "40px",
                padding: "0px 0px",
                fontSize: "14px",
                backgroundColor: mode === "dark" ? "#2990DF" : "#2990DF",
              }}
              color="primary"
              onClick={handleSave}
              disabled={disabledSave}
            >
              {saving ? (
                <>
                  <Spinner size="sm" /> <span className="ms-1">Saving</span>
                </>
              ) : (
                "Save Place ID"
              )}
            </PrimaryButton>
            <PrimaryButton
              style={{
                width: "60px",
                height: "40px",
                padding: "0px 0px",
                fontSize: "14px",
                backgroundColor: "transparent",
                border: "1px solid",
                borderColor: mode === "dark" ? "#fff" : "#000",
                color: mode === "dark" ? "#fff" : "#000",
              }}
              outline
              onClick={handleClear}
              disabled={saving}
            >
              Clear
            </PrimaryButton>
          </Col>
        </Row>

        {/* Local snackbars for quick feedback; your global SnackbarComponent also listens to redux flags */}
        <SnackbarComponent success={showSnackOk} err={showSnackErr} />

        {/**
         * Usage example (in OrganizationForm.jsx):
         * <OrganizationPlaceId
         *   organizationId={organization.id}
         *   initialPlaceId={organization.googlePlaceId}
         *   onSaved={(pid) => console.log("saved place id", pid)}
         * />
         */}
      </CardBody>
    </Card>
  )
}

OrganizationPlaceId.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  initialPlaceId: PropTypes.string,
  onSaved: PropTypes.func,
}

export default OrganizationPlaceId
